import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Users, Heart, Sparkles, Calendar, MapPin, Shirt, Mail, Phone, Facebook, Instagram, Twitter, Youtube } from 'lucide-react';
import { mockData } from '../mock';
import CountdownTimer from '../components/CountdownTimer';
import SpeakerCard from '../components/SpeakerCard';
import ExperienceCard from '../components/ExperienceCard';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail
      });
      
      toast.success(response.data.message);
      setNewsletterEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
      console.error('Newsletter error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openInquiryModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/inquiries`, {
        ...formData,
        type: modalType
      });
      
      toast.success(response.data.message);
      closeModal();
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
      console.error('Inquiry error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden pt-16" id="home">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1648260029310-5f1da359af9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxjcm93ZCUyMGxpZ2h0c3xlbnwwfHx8fDE3NjEzMTY3MzZ8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Faith. Culture. Purpose. <span className="text-[#d97706]">Known.</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200">
            The ultimate experience where belief meets impact.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button size="lg" className="bg-gradient-to-r from-[#d97706] to-[#f59e0b] hover:from-[#b45309] hover:to-[#d97706] text-white text-lg px-8 py-6 shadow-[0_0_20px_rgba(217,119,6,0.5)] hover:shadow-[0_0_30px_rgba(217,119,6,0.7)] transition-all duration-300">
              Buy Tickets
            </Button>
            <a href="/experience">
              <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white text-lg px-8 py-6 backdrop-blur-sm hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all duration-300">
                Discover the Experience
              </Button>
            </a>
          </div>

          <CountdownTimer targetDate={mockData.event.countdown} />
          
          <div className="mt-8 flex items-center justify-center gap-6 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{mockData.event.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span>{mockData.event.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-[#1e3a8a]">
            Who It's For
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.whoItsFor.map((item, index) => (
              <Card key={item.id} className="border-none shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a8a]/5 to-[#3b82f6]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardContent className="p-8 text-center relative z-10">
                  <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-full flex items-center justify-center shadow-lg group-hover:shadow-[0_0_20px_rgba(30,58,138,0.5)] transition-all duration-500 group-hover:scale-110">
                    {index === 0 && <Users className="w-8 h-8 text-white" />}
                    {index === 1 && <Heart className="w-8 h-8 text-white" />}
                    {index === 2 && <Sparkles className="w-8 h-8 text-white" />}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-[#1e3a8a] group-hover:text-[#2563eb] transition-colors duration-300">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Speakers */}
      <section id="speakers" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-[#1e3a8a]">
            Featured Speakers
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            Learn from world-class leaders and visionaries
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.speakers.map((speaker) => (
              <SpeakerCard key={speaker.id} speaker={speaker} />
            ))}
          </div>
        </div>
      </section>

      {/* Powered By - Partners & Sponsors */}
      <section className="py-16 bg-gradient-to-r from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4">
          <h3 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-8">
            Powered By
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {mockData.partners.map((partner) => (
              <div
                key={partner.id}
                className="grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300 hover:scale-110"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-12 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Experience */}
      <section id="experience" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-[#1e3a8a]">
            The Experience
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            Immerse yourself in transformative moments
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {mockData.experiences.map((experience) => (
              <ExperienceCard key={experience.id} experience={experience} />
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-20 bg-[#1e3a8a] text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Get Involved
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.involvement.map((item) => (
              <Card key={item.id} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-500 hover:-translate-y-3 hover:shadow-[0_10px_40px_rgba(217,119,6,0.3)] group">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-[#d97706] transition-colors duration-300">{item.title}</h3>
                  <p className="text-gray-200 mb-6 leading-relaxed">{item.description}</p>
                  <Button 
                    variant="outline" 
                    className="bg-[#d97706] hover:bg-[#b45309] text-white border-none w-full shadow-lg hover:shadow-[0_0_20px_rgba(217,119,6,0.5)] transition-all duration-300"
                    onClick={() => openInquiryModal(item.title.toLowerCase().replace('become a ', '').replace('be a ', ''))}
                  >
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Join the Movement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-[#1e3a8a]">
            Join the Movement
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.movement.map((item) => (
              <Card key={item.id} className="border-2 border-gray-200 hover:border-[#d97706] transition-all duration-500 hover:shadow-[0_10px_30px_rgba(217,119,6,0.2)] cursor-pointer group hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold mb-3 text-[#1e3a8a] group-hover:text-[#d97706] transition-colors duration-300">{item.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                  <Button 
                    className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white w-full shadow-md hover:shadow-[0_0_15px_rgba(30,58,138,0.4)] transition-all duration-300"
                    onClick={() => {
                      if (item.title === 'Newsletter Signup') {
                        document.querySelector('#newsletter-footer')?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        const type = item.title.toLowerCase().replace('join a ', '').replace('get ', '').replace('sign up for ', '');
                        openInquiryModal(type);
                      }
                    }}
                  >
                    Sign Up
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Conference Info */}
      <section id="info" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-[#1e3a8a]">
            Conference Info
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {mockData.conferenceInfo.map((info) => (
              <a
                key={info.id}
                href={info.link}
                className="block p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-[#1e3a8a] transition-all duration-500 hover:shadow-[0_8px_25px_rgba(30,58,138,0.15)] text-center group hover:-translate-y-1 hover:bg-gradient-to-br hover:from-white hover:to-blue-50"
              >
                <h3 className="text-lg font-bold text-[#1e3a8a] group-hover:text-[#d97706] transition-colors duration-300">{info.title}</h3>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Merch Banner */}
      <section className="py-16 bg-gradient-to-r from-[#1e3a8a] via-[#2563eb] to-[#1e3a8a] text-white">
        <div className="container mx-auto px-4 text-center">
          <Shirt className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            KNOWN CON Official Merch
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Wear your faith with pride
          </p>
          <Button size="lg" className="bg-[#d97706] hover:bg-[#b45309] text-white text-lg px-10 py-6">
            Shop Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-5 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-[#d97706]">KNOWN CON</h3>
              <p className="text-gray-400 mb-4">Where belief meets impact.</p>
              <div className="flex gap-3">
                <a href="#" className="text-gray-400 hover:text-[#d97706] transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#d97706] transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#d97706] transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#d97706] transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Event</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#speakers" className="hover:text-[#d97706] transition-colors">Speakers</a></li>
                <li><a href="#schedule" className="hover:text-[#d97706] transition-colors">Schedule</a></li>
                <li><a href="#experience" className="hover:text-[#d97706] transition-colors">Experience</a></li>
                <li><a href="#merch" className="hover:text-[#d97706] transition-colors">Merch Store</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Get Involved</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#sponsor" className="hover:text-[#d97706] transition-colors">Become a Sponsor</a></li>
                <li><a href="#vendor" className="hover:text-[#d97706] transition-colors">Become a Vendor</a></li>
                <li><a href="#volunteer" className="hover:text-[#d97706] transition-colors">Volunteer</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@knowncon.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>(555) 123-4567</span>
                </li>
                <li><a href="#faq" className="hover:text-[#d97706] transition-colors">FAQs</a></li>
                <li><a href="#travel" className="hover:text-[#d97706] transition-colors">Hotel & Travel</a></li>
              </ul>
            </div>
            <div id="newsletter-footer">
              <h4 className="font-bold mb-4 text-lg">Newsletter</h4>
              <form onSubmit={handleNewsletterSubmit} className="space-y-2">
                <Input 
                  type="email" 
                  placeholder="Your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#d97706] hover:bg-[#b45309]"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </form>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2025 KNOWN CON. All rights reserved.
              </p>
              <div className="flex gap-4 text-sm text-gray-400">
                <a href="#privacy" className="hover:text-[#d97706] transition-colors">Privacy Policy</a>
                <span>•</span>
                <a href="#terms" className="hover:text-[#d97706] transition-colors">Terms & Conditions</a>
                <span>•</span>
                <a href="#contact" className="hover:text-[#d97706] transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Inquiry Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <h3 className="text-2xl font-bold mb-2 text-[#1e3a8a] capitalize">
              {modalType.replace('_', ' ')}
            </h3>
            <p className="text-gray-600 mb-6">Please fill out the form below and we'll be in touch soon!</p>
            
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Name *</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Phone</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Message *</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  disabled={isSubmitting}
                  rows={4}
                  className="w-full"
                  placeholder="Tell us more about your interest..."
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-[#1e3a8a] hover:bg-[#1e40af]"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
